/**
 * @file Recherche des prochaines dates réservables pour le bandeau du calendrier.
 * @module packages.common.components.table-reservation.hooks.useNextAvailability
 *
 * Interroge la source de données du flow (`getNextAvailableDates`) quand le
 * mois affiché ne propose aucune place, et met en cache chaque réponse par
 * service et convives. Aucun jour de départ n'est envoyé : le serveur balaie
 * toujours depuis son aujourd'hui (fuseau établissement), si bien que revenir
 * sur un autre mois sans place ne relance rien. Une requête en vol est annulée
 * dès que la clé change ; une réponse obsolète n'écrit jamais l'état. L'erreur
 * est silencieuse : le bandeau disparaît, le calendrier reste utilisable.
 */

import {useState, useEffect, useRef} from 'react';
import {logger} from 'packages/common/utils/clientLogger';

import {useReservationData} from '../context/ReservationDataContext';

export const NEXT_AVAILABILITY_STATUS = Object.freeze({
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
});

/**
 * Charge les prochaines dates réservables à partir d'un jour donné.
 *
 * @param {Object} params
 * @param {boolean} params.enabled - Déclenche la recherche (mois affiché sans place)
 * @param {string} [params.service] - Nom du service sélectionné
 * @param {number} [params.partySize] - Nombre de convives
 * @return {{status: string, result: (Object|null)}} Statut et dernière réponse
 *   `{dates, horizon, timezone}` pour la clé courante
 */
export function useNextAvailability({enabled, service, partySize}) {
  const {getNextAvailableDates} = useReservationData();
  const [state, setState] = useState({status: NEXT_AVAILABILITY_STATUS.IDLE, result: null, key: null});
  const cacheRef = useRef({});

  const key = enabled && service && partySize ? `${service}-${partySize}` : null;

  useEffect(() => {
    if (!key) {
      setState({status: NEXT_AVAILABILITY_STATUS.IDLE, result: null, key: null});
      return undefined;
    }

    const cached = cacheRef.current[key];
    if (cached) {
      setState({status: NEXT_AVAILABILITY_STATUS.READY, result: cached, key});
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState({status: NEXT_AVAILABILITY_STATUS.LOADING, result: null, key});

    const fetchNextDates = async () => {
      try {
        const result = await getNextAvailableDates({service, partySize}, {signal: controller.signal});
        if (cancelled) return;
        cacheRef.current[key] = result;
        setState({status: NEXT_AVAILABILITY_STATUS.READY, result, key});
      } catch (error) {
        if (cancelled || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return;
        logger.error('Erreur chargement prochaines dates disponibles :', error.message);
        setState({status: NEXT_AVAILABILITY_STATUS.ERROR, result: null, key});
      }
    };

    fetchNextDates();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key, service, partySize, getNextAvailableDates]);

  // L'état d'une clé précédente ne doit jamais s'afficher sous une nouvelle clé.
  if (state.key !== key) {
    return {status: key ? NEXT_AVAILABILITY_STATUS.LOADING : NEXT_AVAILABILITY_STATUS.IDLE, result: null};
  }

  return {status: state.status, result: state.result};
}
